import {Button, Col, Container, Modal, Stack, Tab, Tabs} from 'react-bootstrap';
import CreateShareRecords from './components/CreateShareRecords';
import {useEffect, useRef, useState} from 'react';
import SharedByMeList from './components/SharedByMeList';
import SharedWithMeList from './components/SharedWithMeList';
import {getSharedWithMeList, revokeShareRecord} from '../../services/SharedRecordsApi';
import Loader from '../../common/Loader';
import Snackbar from '../../common/Snackbar';
import { SHARE_RECORDS_STATUS } from '../../common/Constant';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/Store';
import { SHARED_BY_ME_ACTIONS } from '../../common/EventConstant';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';

type ShareRecord = {
  actions: {
    Edit?: string;
    View?: string;
  };
  caseId: string;
  caseIdName: string;
  caseName: string;
  expiryDate: string | null;
  expiryDateNew: string;
  responseCode: number;
  responseMessage: string;
  shareId: number;
  shareStatus: string;
  sharedBy: string;
  sharedBy_On: string;
  sharedOn: string;
  sharedWithEmails: string;
  sharedWithUserNames: string | null;
  status?: string;
  subjectFullName: string;
};

const sharedByMeActions = [
  { label: "Shared By Me" },
  { label: "Edit" },
  { label: "Revoke Action" },
  { label: "Record Details" },
  { label: "Share Internal" },
  { label: "Share External" },
  { label: "Share Expiration" },
  { label: "Edit Record Details" },
  { label: "Share Records" },
];

const sharedByMeActionFeatureMap: Record<string, string> = {
  "Shared By Me": SHARED_BY_ME_ACTIONS.MAIN,
  "Edit": SHARED_BY_ME_ACTIONS.EDIT,
  "Share Records": SHARED_BY_ME_ACTIONS.SHARE_RECORDS,
  "Revoke Action": SHARED_BY_ME_ACTIONS.REVOKE,
  "Record Details": SHARED_BY_ME_ACTIONS.RECORD_DETAILS,
  "Share Internal": SHARED_BY_ME_ACTIONS.SHARE_INTERNAL,
  "Share External": SHARED_BY_ME_ACTIONS.SHARE_EXTERNAL,
  "Share Expiration": SHARED_BY_ME_ACTIONS.SHARE_EXPIRATION,
  "Edit Record Details": SHARED_BY_ME_ACTIONS.EDIT_RECORD_DETAILS,
};

const ShareRecords = () => {
  const {filterByAccess} = useFeatureAccess();
  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const [open, setOpen] = useState<boolean>(false);
  const [headerActiveTab, setHeaderActiveTab] = useState('Shared By Me');
  const [selectedRowData, setSelectedRowData] = useState<ShareRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState('');
  const [showModalNew, setShowModalNew] = useState(false);
  const [listData, setListData] = useState<any[]>([]);
  const filteredActionAccess = filterByAccess(sharedByMeActions, sharedByMeActionFeatureMap);
  const actionsAccess = filteredActionAccess?.map(opt => opt.label);
  const hasSharedByMe = actionsAccess.includes("Shared By Me");
  const latestRequestRef = useRef(0);
  
  const fetchShareListData = async (
    loggedInUserid: number | null,
    query: string = "",
  ) => {
    const requestId = ++latestRequestRef.current;
    try {
      const shareData = await getSharedWithMeList(loggedInUserid, query);
      if (requestId !== latestRequestRef.current) return;
      setListData(shareData || []);
    } catch (error) {
      console.error("failed to fetch data:", error);
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if(loggedInUserid){
      fetchShareListData(loggedInUserid, '');
    }
  }, []);

  const handleAddUserClick = () => {
    setSelectedRowData(null);
    setOpen(true);
  };

  const handleHeaderTabChange = (key: string | null) => {
    if (key === 'Shared By Me') {
      setHeaderActiveTab('Shared By Me');
    } else if (key === 'Shared With Me') {
      setHeaderActiveTab('Shared With Me');
    }
  };

  const handleRowAction = (row: any, tabStatus: string) => {
    setOpen(true);
    setSelectedRowData(row);
    setHeaderActiveTab(
      tabStatus === 'SharedByMe' ? 'Shared By Me' : 'Shared With Me',
    );
  };

  const handleTabChange = () => {
    setHeaderActiveTab('Shared By Me');
    setSelectedRowData(null);
    setOpen(false);
    setSnackbarOpen(false);
  };

  const handleRevokeButton = async () => {
    if (!selectedRowData?.shareId) return;
    try {
      setLoading(true);
      const revorkData = await revokeShareRecord(selectedRowData?.shareId);
      if (revorkData?.result) {
        setSnackbarMessage('Shared Record has been Revoked.');
        setError('success');
        setSnackbarOpen(true);
        setShowModalNew(false);
        setTimeout(() => {
          handleTabChange();
        }, 1000);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokedButton = () => {
    setShowModalNew(true);
  };

  const handleCloseModalNew = () => {
    setShowModalNew(false);
  };

  const isDisable = selectedRowData?.status === SHARE_RECORDS_STATUS.EXPIRED || selectedRowData?.status === SHARE_RECORDS_STATUS.REVOKED || selectedRowData?.actions?.View === 'View';

  return (
    <>
      <div className="breadcrumbg pb-0">
        <Container fluid className="px-5">
          <Stack direction="horizontal" gap={2}>
            <Col className="pt-0 flex-column d-flex align-items-start fade-in-left">
              <h4>Share Records</h4>
            </Col>
            {!open && headerActiveTab === "Shared By Me" && !selectedRowData && (
              <div className="p-2 ms-auto">
                <Button
                  onClick={handleAddUserClick}
                  className="btn btn-primary d-flex align-items-center fade-in-right"
                >
                  <span className="material-icons me-2">add</span>
                  Share Records
                </Button>
              </div>
            )}
            {selectedRowData && !isDisable && actionsAccess.includes("Revoke Action") && (
              <div className="p-2 ms-auto">
                <Button
                  onClick={handleRevokedButton}
                  className="btn btn-outline-danger d-flex align-items-center fade-in-right"
                  disabled={isDisable}
                >
                  <span className="material-icons material-symbols-outlined me-2">
                    undo
                  </span>
                  Revoke
                </Button>
              </div>
            )}
          </Stack>
        </Container>
      </div>
      <div className="main_wrapper_section pills_wrapper ">
        <div className="px-5">
          {open ? (
            <>
              <Loader isLoading={loading} />
              <Snackbar
                message={snackbarMessage}
                open={snackbarOpen}
                onClose={() => setSnackbarOpen(false)}
                severity={error}
              />
              
              <CreateShareRecords
                selectedRowData={selectedRowData}
                onTabChange={handleTabChange}
                headerActiveTab={headerActiveTab}
                actionsAccess={actionsAccess}
              />
            </>
          ) : (
            <Tabs
              defaultActiveKey="Shared By Me"
              activeKey={headerActiveTab}
              onSelect={handleHeaderTabChange}
              className="mt-3 mb-3 fade-in-left"
            >
              {hasSharedByMe && 
              <Tab eventKey="Shared By Me" title="Shared By Me">
                <SharedByMeList onRowSelect={handleRowAction} headerActiveTab={headerActiveTab} actionsAccess={actionsAccess}/>
              </Tab>
              }
              <Tab eventKey="Shared With Me" title="Shared With Me" disabled={listData?.length == 0}>
                <SharedWithMeList onRowSelect={handleRowAction} headerActiveTab={headerActiveTab}/>
              </Tab>
            </Tabs>
          )}
        </div>
      </div>
      <Modal show={showModalNew} onHide={handleCloseModalNew}>
        <Modal.Body className="fw-bold text-left modelheading ">
          <div>Revoke shared access to these records?</div>
        </Modal.Body>
        <Modal.Footer className="border-top d-flex justify-content-end">
          <Button
            variant="outline-info"
            className="d-flex align-items-end"
            onClick={handleCloseModalNew}
          >
            <span className="material-icons mr-2">close</span> Cancel
          </Button>
          <Button
            onClick={handleRevokeButton}
            variant="primary"
            className="ms-3 d-flex align-items-center"
          >
            <span className="material-icons mx-1">check</span> Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ShareRecords;
